/**
 * Geometry for the daily-peak chart, shared by the interactive React chart on
 * the Existing Load tab and the static figure printed on the report. Both
 * consume the same primitives, so the inspector's figure is the contractor's
 * chart, not a re-drawing of it.
 *
 * Marks follow the house style for charts: a 2 px line over a 10% wash,
 * hairline solid gridlines, an 8 px+ peak marker with a surface ring, the
 * extreme direct-labelled and nothing else, winter shaded because it is the
 * season the concurrency logic keys on.
 *
 * `unlocked` decides whether any kilowatt value is drawn. Before unlock the
 * shape, the dates and the season are shown; the scale and the peak's value
 * are not — the measured peak is paid content (context/ux.md LOCKED #1).
 */
import type { DemandSeries } from './demandSeries';
import { formatMonthDay } from './demandSeries';

export interface ChartPalette {
  line: string;
  area: string;
  grid: string;
  base: string;
  text: string;
  textStrong: string;
  band: string;
  marker: string;
  warn: string;
  ring: string;
}

/** Tailwind values the app already uses: blue-600/700, gray-200/300/500/900, amber-600. */
export const APP_PALETTE: ChartPalette = {
  line: '#2563eb',
  area: 'rgba(37,99,235,0.10)',
  grid: '#e5e7eb',
  base: '#d1d5db',
  text: '#6b7280',
  textStrong: '#111827',
  band: 'rgba(17,24,39,0.035)',
  marker: '#1d4ed8',
  warn: '#d97706',
  ring: '#ffffff',
};

/** Ink on white, for the report and for a printer. */
export const PRINT_PALETTE: ChartPalette = {
  line: '#111111',
  area: 'rgba(17,17,17,0.07)',
  grid: '#cfcfcf',
  base: '#666666',
  text: '#333333',
  textStrong: '#111111',
  band: 'rgba(0,0,0,0.035)',
  marker: '#111111',
  warn: '#7a5200',
  ring: '#ffffff',
};

export type Primitive =
  | { kind: 'rect'; x: number; y: number; width: number; height: number; fill: string }
  | { kind: 'line'; x1: number; y1: number; x2: number; y2: number; stroke: string; strokeWidth: number; linecap?: 'round' }
  | { kind: 'path'; d: string; fill: string; stroke?: string; strokeWidth?: number; transform?: string }
  | { kind: 'circle'; cx: number; cy: number; r: number; fill: string; stroke: string; strokeWidth: number }
  | {
      kind: 'text';
      x: number;
      y: number;
      text: string;
      fill: string;
      fontSize: number;
      fontWeight?: number;
      anchor?: 'start' | 'middle' | 'end';
      letterSpacing?: string;
      tabular?: boolean;
    };

export interface ChartLayout {
  width: number;
  height: number;
  primitives: Primitive[];
  /** Day index (year) or hour (peak day) to x. */
  x: (v: number) => number;
  y: (kw: number) => number;
  plot: { left: number; right: number; top: number; bottom: number };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const LOCK_PATH =
  'M2 6.5h8a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1v-4a1 1 0 0 1 1-1zm1.5 0V4.5a2.5 2.5 0 0 1 5 0v2';

const MONTH_STRIDES = [1, 2, 3, 6, 12];

// A deliberately generous width for an 11px label (7px a character), so the
// collision check holds for the app's sans and the report's Times alike.
const labelWidth = (text: string) => text.length * 7;

// Labels on every `stride`-th calendar month, aligned so January is always one
// of them. A multi-year record puts the year on a second row, under the first
// label and every January.
function monthLabels(months: DemandSeries['months'], stride: number, multiYear: boolean) {
  const out: { start: number; end: number; month: string; year: string }[] = [];
  for (const m of months) {
    if (m.end - m.start < 12) continue;
    const mi = Number(m.key.slice(5, 7)) - 1;
    if (mi % stride !== 0) continue;
    const year = multiYear && (out.length === 0 || mi === 0) ? m.key.slice(0, 4) : '';
    out.push({ start: m.start, end: m.end, month: MONTHS[mi], year });
  }
  return out;
}

function rowFits(row: { cx: number; text: string }[]) {
  return row.every((l, k) => k === 0 || l.cx - row[k - 1].cx >= (labelWidth(l.text) + labelWidth(row[k - 1].text)) / 2 + 6);
}

/** Approximate advance of one character of the 11px gap label, in layout px. */
const GAP_LABEL_CHAR_PX = 6.2;

export interface YearLayoutOptions {
  width: number;
  height?: number;
  unlocked: boolean;
  palette: ChartPalette;
}

export function layoutDemandYear(series: DemandSeries, opts: YearLayoutOptions): ChartLayout {
  const P = opts.palette;
  const days = series.days;
  const N = days.length;
  const W = Math.max(280, Math.round(opts.width));
  const H = opts.height ?? (W < 560 ? 220 : 260);
  const ML = opts.unlocked ? 48 : 20;
  const MR = 16;
  const MT = 30;
  const multiYear = days[0].date.slice(0, 4) !== days[N - 1].date.slice(0, 4);
  const MB = multiYear ? 40 : 28;
  const pw = W - ML - MR;
  const ph = H - MT - MB;
  const x = (i: number) => ML + (N > 1 ? (i / (N - 1)) * pw : pw / 2);
  const y = (v: number) => MT + (1 - v / series.ymax) * ph;
  const prims: Primitive[] = [];

  // Season runs: winter shaded, every run long enough to carry a label.
  const runs: { s: string; start: number; end: number }[] = [];
  days.forEach((d, i) => {
    const m = Number(d.date.slice(5, 7));
    const s = m === 12 || m <= 2 ? 'Winter' : m <= 5 ? 'Spring' : m <= 8 ? 'Summer' : 'Fall';
    const last = runs[runs.length - 1];
    if (!last || last.s !== s) runs.push({ s, start: i, end: i + 1 });
    else last.end = i + 1;
  });
  for (const r of runs) {
    if (r.s === 'Winter') {
      prims.push({ kind: 'rect', x: x(r.start), y: MT - 18, width: Math.max(0, x(r.end - 1) - x(r.start)), height: ph + 18, fill: P.band });
    }
    // A label needs room on screen, not just days on the calendar: at phone
    // width a 20-day run is narrower than the word.
    if (r.end - r.start >= 20 && x(r.end - 1) - x(r.start) >= 64) {
      prims.push({ kind: 'text', x: (x(r.start) + x(r.end - 1)) / 2, y: MT - 6, text: r.s.toUpperCase(), fill: P.text, fontSize: 11, anchor: 'middle', letterSpacing: '.04em' });
    }
  }

  // Gridlines are hairline and solid; the numbers exist only after unlock.
  for (let v = series.step; v <= series.ymax; v += series.step) {
    prims.push({ kind: 'line', x1: ML, y1: y(v), x2: W - MR, y2: y(v), stroke: P.grid, strokeWidth: 1 });
    if (opts.unlocked) {
      prims.push({ kind: 'text', x: ML - 8, y: y(v) + 4, text: v + (v === series.ymax ? ' kW' : ''), fill: P.text, fontSize: 11, anchor: 'end', tabular: true });
    }
  }
  prims.push({ kind: 'line', x1: ML, y1: y(0), x2: W - MR, y2: y(0), stroke: P.base, strokeWidth: 1 });

  // Month ticks on every month; labels on every month, every 2nd, every 3rd
  // (6th, 12th for very long records), whichever is the densest that does not
  // collide.
  for (const m of series.months) {
    const mx = x(m.start);
    prims.push({ kind: 'line', x1: mx, y1: y(0), x2: mx, y2: y(0) + 4, stroke: P.base, strokeWidth: 1 });
  }
  for (const stride of MONTH_STRIDES) {
    const labels = monthLabels(series.months, stride, multiYear).map(l => ({ ...l, cx: (x(l.start) + x(l.end - 1)) / 2 }));
    const fits =
      rowFits(labels.map(l => ({ cx: l.cx, text: l.month }))) &&
      rowFits(labels.filter(l => l.year).map(l => ({ cx: l.cx, text: l.year })));
    if (!fits && stride !== MONTH_STRIDES[MONTH_STRIDES.length - 1]) continue;
    for (const l of labels) {
      prims.push({ kind: 'text', x: l.cx, y: y(0) + 18, text: l.month, fill: P.text, fontSize: 11, anchor: 'middle' });
      if (l.year) prims.push({ kind: 'text', x: l.cx, y: y(0) + 31, text: l.year, fill: P.text, fontSize: 11, anchor: 'middle' });
    }
    break;
  }

  // The wash and the line.
  let dArea = `M${x(0).toFixed(1)} ${y(0).toFixed(1)}`;
  let dLine = '';
  for (let i = 0; i < N; i++) {
    const px = x(i).toFixed(1);
    const py = y(days[i].max).toFixed(1);
    dArea += ` L${px} ${py}`;
    dLine += `${i === 0 ? 'M' : ' L'}${px} ${py}`;
  }
  dArea += ` L${x(N - 1).toFixed(1)} ${y(0).toFixed(1)} Z`;
  prims.push({ kind: 'path', d: dArea, fill: P.area });
  prims.push({ kind: 'path', d: dLine, fill: 'none', stroke: P.line, strokeWidth: 2 });

  // Gaps: a warning mark with its label, never colour alone. Labels are laid
  // longest gap first: one that would run past the plot turns to the left of
  // its mark, and one that would collide with a label already placed is left
  // off — two gaps a day apart (an outage and its trickle) printed on top of
  // each other. Every gap keeps its mark; the checks line below the chart
  // counts them all.
  const placed: Array<[number, number]> = [];
  const gapMarks = series.gaps
    .slice(0, 3)
    .map(g => ({ g, gi: days.findIndex(d => d.date === g.date) }))
    .filter(m => m.gi >= 0)
    .sort((a, b) => b.g.minutes - a.g.minutes);
  for (const { g, gi } of gapMarks) {
    const gx = x(gi);
    prims.push({ kind: 'line', x1: gx, y1: y(0) - 14, x2: gx, y2: y(0), stroke: P.warn, strokeWidth: 2, linecap: 'round' });
    const text = `⚠ ${g.minutes}-min gap · ${formatMonthDay(g.date)}`;
    const w = text.length * GAP_LABEL_CHAR_PX;
    const right = gx + 6 + w <= ML + pw;
    const span: [number, number] = right ? [gx + 6, gx + 6 + w] : [gx - 6 - w, gx - 6];
    if (placed.some(([a, b]) => span[0] < b + 8 && a < span[1] + 8)) continue;
    placed.push(span);
    prims.push({ kind: 'text', x: right ? gx + 6 : gx - 6, y: y(0) - 6, text, fill: P.warn, fontSize: 11, fontWeight: 600, anchor: right ? 'start' : 'end' });
  }

  // The peak: the one direct label on the chart. The value only after unlock.
  const pxk = x(series.peakIndex);
  const pyk = y(series.peak.kw);
  prims.push({ kind: 'circle', cx: pxk, cy: pyk, r: 6, fill: P.marker, stroke: P.ring, strokeWidth: 2 });
  const right = pxk < W * 0.7;
  const lx = right ? pxk + 12 : pxk - 12;
  const anchor = right ? 'start' : 'end';
  const when = `Peak · ${formatMonthDay(series.peak.date)} · ${series.peak.timeLabel} · ${series.peak.season}`;
  if (opts.unlocked) {
    prims.push({ kind: 'text', x: lx, y: pyk - 2, text: `${series.peak.kw.toFixed(2)} kW`, fill: P.textStrong, fontSize: 14, fontWeight: 700, anchor });
    prims.push({ kind: 'text', x: lx, y: pyk + 14, text: when, fill: P.text, fontSize: 11, anchor });
  } else {
    prims.push({ kind: 'text', x: lx, y: pyk - 2, text: when, fill: P.textStrong, fontSize: 13, fontWeight: 700, anchor });
    const lockX = right ? lx : lx - 150;
    prims.push({ kind: 'path', d: LOCK_PATH, fill: 'none', stroke: P.text, strokeWidth: 1.3, transform: `translate(${lockX.toFixed(1)} ${(pyk + 4).toFixed(1)})` });
    prims.push({ kind: 'text', x: lockX + 16, y: pyk + 15, text: 'Value shown after unlock', fill: P.text, fontSize: 11 });
  }

  return { width: W, height: H, primitives: prims, x, y, plot: { left: ML, right: W - MR, top: MT, bottom: y(0) } };
}

export interface DayLayoutOptions {
  width: number;
  height?: number;
  palette: ChartPalette;
}

/** The peak day's readings, hour 0 to 24. Only ever drawn unlocked. */
export function layoutPeakDay(series: DemandSeries, opts: DayLayoutOptions): ChartLayout {
  const P = opts.palette;
  const pts = series.peakDay;
  const W = Math.max(260, Math.round(opts.width));
  const H = opts.height ?? 220;
  const ML = 48, MR = 16, MT = 16, MB = 28;
  const pw = W - ML - MR;
  const ph = H - MT - MB;
  const x = (h: number) => ML + (h / 24) * pw;
  const y = (v: number) => MT + (1 - v / series.ymax) * ph;
  const prims: Primitive[] = [];

  for (let v = series.step; v <= series.ymax; v += series.step) {
    prims.push({ kind: 'line', x1: ML, y1: y(v), x2: W - MR, y2: y(v), stroke: P.grid, strokeWidth: 1 });
    prims.push({ kind: 'text', x: ML - 8, y: y(v) + 4, text: v + (v === series.ymax ? ' kW' : ''), fill: P.text, fontSize: 11, anchor: 'end', tabular: true });
  }
  prims.push({ kind: 'line', x1: ML, y1: y(0), x2: W - MR, y2: y(0), stroke: P.base, strokeWidth: 1 });
  const ticks: Array<[number, string]> = [[0, '12 AM'], [6, '6 AM'], [12, '12 PM'], [18, '6 PM'], [24, '12 AM']];
  for (const [h, label] of ticks) {
    prims.push({ kind: 'line', x1: x(h), y1: y(0), x2: x(h), y2: y(0) + 4, stroke: P.base, strokeWidth: 1 });
    prims.push({ kind: 'text', x: x(h), y: y(0) + 18, text: label, fill: P.text, fontSize: 11, anchor: h === 0 ? 'start' : h === 24 ? 'end' : 'middle' });
  }

  if (pts.length > 0) {
    let dArea = `M${x(pts[0].hour).toFixed(1)} ${y(0).toFixed(1)}`;
    let dLine = '';
    let pk = 0;
    pts.forEach((p, i) => {
      if (p.kw > pts[pk].kw) pk = i;
      const px = x(p.hour).toFixed(1);
      const py = y(p.kw).toFixed(1);
      dArea += ` L${px} ${py}`;
      dLine += `${i === 0 ? 'M' : ' L'}${px} ${py}`;
    });
    dArea += ` L${x(pts[pts.length - 1].hour).toFixed(1)} ${y(0).toFixed(1)} Z`;
    prims.push({ kind: 'path', d: dArea, fill: P.area });
    prims.push({ kind: 'path', d: dLine, fill: 'none', stroke: P.line, strokeWidth: 2 });
    const pxk = x(pts[pk].hour);
    const pyk = y(pts[pk].kw);
    const right = pxk < W * 0.65;
    prims.push({ kind: 'circle', cx: pxk, cy: pyk, r: 6, fill: P.marker, stroke: P.ring, strokeWidth: 2 });
    prims.push({ kind: 'text', x: right ? pxk + 12 : pxk - 12, y: pyk + 4, text: `${pts[pk].kw.toFixed(2)} kW · ${series.peak.timeLabel}`, fill: P.textStrong, fontSize: 13, fontWeight: 700, anchor: right ? 'start' : 'end' });
  }

  return { width: W, height: H, primitives: prims, x, y, plot: { left: ML, right: W - MR, top: MT, bottom: y(0) } };
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function num(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

/**
 * The layout as an SVG string, for HTML that is assembled as text (the report).
 * Text takes the font passed in so the figure sets in the report's own face.
 */
export function layoutToSvgString(layout: ChartLayout, opts: { fontFamily: string; ariaLabel: string }): string {
  const parts: string[] = [];
  for (const p of layout.primitives) {
    switch (p.kind) {
      case 'rect':
        parts.push(`<rect x="${num(p.x)}" y="${num(p.y)}" width="${num(p.width)}" height="${num(p.height)}" fill="${p.fill}"/>`);
        break;
      case 'line':
        parts.push(`<line x1="${num(p.x1)}" y1="${num(p.y1)}" x2="${num(p.x2)}" y2="${num(p.y2)}" stroke="${p.stroke}" stroke-width="${p.strokeWidth}"${p.linecap ? ` stroke-linecap="${p.linecap}"` : ''}/>`);
        break;
      case 'path':
        parts.push(`<path d="${p.d}" fill="${p.fill}"${p.stroke ? ` stroke="${p.stroke}" stroke-width="${p.strokeWidth ?? 1}" stroke-linejoin="round" stroke-linecap="round"` : ''}${p.transform ? ` transform="${p.transform}"` : ''}/>`);
        break;
      case 'circle':
        parts.push(`<circle cx="${num(p.cx)}" cy="${num(p.cy)}" r="${p.r}" fill="${p.fill}" stroke="${p.stroke}" stroke-width="${p.strokeWidth}"/>`);
        break;
      case 'text':
        parts.push(`<text x="${num(p.x)}" y="${num(p.y)}" fill="${p.fill}" font-size="${p.fontSize}"${p.fontWeight ? ` font-weight="${p.fontWeight}"` : ''}${p.anchor ? ` text-anchor="${p.anchor}"` : ''}${p.letterSpacing ? ` letter-spacing="${p.letterSpacing}"` : ''}>${esc(p.text)}</text>`);
        break;
    }
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${layout.width} ${layout.height}" width="${layout.width}" height="${layout.height}" role="img" aria-label="${esc(opts.ariaLabel)}" style="font-family: ${esc(opts.fontFamily)}; max-width: 100%; height: auto; display: block;">${parts.join('')}</svg>`;
}
